import { Logger } from '@/utils/logging';

import type { Fbq } from './metaPixel.types';

const FBEVENTS_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

/** Meta pixel ids are numeric; anything else is rejected before reaching fbq. */
export const META_PIXEL_ID_PATTERN = /^\d{5,20}$/;

const initializedPixelIds = new Set<string>();

// TypeScript port of Meta's base-code snippet: a queueing stub that
// fbevents.js replaces once it loads.
const installFbq = (): Fbq => {
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(arguments);
    }
  } as Fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = FBEVENTS_SRC;
  document.head.appendChild(script);
  return fbq;
};

/**
 * Loads Meta's pixel script once per page and initializes each brand pixel
 * once. Several brand pixels can share the one fbq, so every event sent to a
 * pixel must use `trackSingle` / `trackSingleCustom` with its id.
 */
export const ensureMetaPixel = (pixelId: string): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!META_PIXEL_ID_PATTERN.test(pixelId)) {
    Logger.warn('[meta-pixel] rejected malformed pixel id', { pixelId });
    return;
  }
  const fbq = window.fbq ?? installFbq();
  if (initializedPixelIds.has(pixelId)) return;
  initializedPixelIds.add(pixelId);
  fbq('init', pixelId);
  fbq('trackSingle', pixelId, 'PageView');
};
